

import { useTranslation } from 'react-i18next';
import { Button, Col, DatePicker, Dropdown, Form, Input, Row, Select, notification } from 'antd';
import { DeleteOutlined, FileExcelOutlined, FilterOutlined, SearchOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import moment from 'moment';

import './BookingsFilterComponent.scss'
import { BookingExportPreset, BookingFilter } from '../../../models/BookingsFilter';
import { useState } from 'react';
import HotelInfo from '../../../models/HotelInfo';
import { GenericModal } from '../../common/Modal/GenericModal';
import { deletePendingBookings, exportBookings } from '../../../api/repositoryEP';
import { saveBlobAsFile } from '../../../api/downloadFile';
import RestApiClient from '../../../api/restApiClient';
import { GetLanguageInUrl, IsLaguagePresentInUrl } from '../../../utils/urls';

const EXPORT_DATE_FORMAT: string = "YYYY-MM-DD";

const restApiClient: RestApiClient = new RestApiClient();

interface BookingsFilterComponentProps {
    onSearch: (filterData: BookingFilter) => void;
    hotelList: Array<HotelInfo>;
    searching: boolean;
}

const BookingsFilterComponent = (props: BookingsFilterComponentProps): JSX.Element => {

    const { onSearch, hotelList, searching } = props;

    const { t } = useTranslation();
    const [form] = Form.useForm();

    const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
    const [deletingPending, setDeletingPending] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);

    const handleDeletePendingBookings = async () => {
        setDeletingPending(true);
        const response = await deletePendingBookings();
        if (response && response?.type === 1 && response?.data == true) {
            setDeleteModalVisible(false);
            form.submit();
        }
        setDeletingPending(false);

    };

    const items = [
        { key: '1', label: t("components.bookings.filter.last-week") },
        { key: '2', label: t("components.bookings.filter.last-month") },
        { key: '3', label: t("components.bookings.filter.last-year") },
        { key: '4', label: t("components.bookings.filter.status-confirmed") },
        { key: '5', label: t("components.bookings.filter.status-cancelled") },
    ];

    const status = [
        { key: "pending", label: t("components.bookings.filter.name-status.pending") },
        { key: "postponed", label: t("components.bookings.filter.name-status.postponed") },
        { key: "active", label: t("components.bookings.filter.name-status.active") },
        { key: "canceled", label: t("components.bookings.filter.name-status.canceled") },
        { key: "error", label: t("components.bookings.filter.name-status.error") },
    ];

    const buildFilter = (values: any): BookingFilter => {
        const str = (v: any) => (v && String(v).trim() !== '' ? String(v).trim() : null);
        const arr = (v: any) => (Array.isArray(v) && v.length > 0 ? v : null);

        return {
            locator: str(values.locator),
            status: str(values.status),
            agency: str(values.agency),
            checkIn: values.checkin ? values.checkin.format("YYYY-MM-DD") : null,
            checkOut: values.checkout ? values.checkout.format("YYYY-MM-DD") : null,
            location: str(values.location),
            name: str(values.name),
            hotel: arr(values.hotels),
            ownLocator: str(values.ownLocator),
            userEmail: str(values.userEmail),
            page: 0,
            pageSize: 10,
        };
    };

    const onFinish = (values: any) => {
        onSearch(buildFilter(values));
    };

    /**
     * Atajos del desplegable. Las claves 1-3 acotan la fecha de creación y las 4-5 el estado.
     * createdTo es INCLUSIVO: es el último día que se quiere incluir, por eso se manda hoy.
     * El controlador ya lo convierte en límite exclusivo por su cuenta.
     */
    const getExportPreset = (key: string): BookingExportPreset | undefined => {
        const today = moment();

        switch (key) {
            case '1':
                return {
                    createdFrom: today.clone().subtract(1, 'week').format(EXPORT_DATE_FORMAT),
                    createdTo: today.clone().format(EXPORT_DATE_FORMAT),
                };
            case '2':
                return {
                    createdFrom: today.clone().subtract(1, 'month').format(EXPORT_DATE_FORMAT),
                    createdTo: today.clone().format(EXPORT_DATE_FORMAT),
                };
            case '3':
                return {
                    createdFrom: today.clone().subtract(1, 'year').format(EXPORT_DATE_FORMAT),
                    createdTo: today.clone().format(EXPORT_DATE_FORMAT),
                };
            case '4':
                return { status: 'active' };
            case '5':
                return { status: 'canceled' };
            default:
                return undefined;
        }
    };

    const getExportFileName = (): string => {
        const name = (t("components.bookings.filter.export-file-name") || 'export').replace(/[\\/:*?"<>|]/g, '_');

        return `${name}_${moment().format(EXPORT_DATE_FORMAT)}.xlsx`;
    };

    /**
     * Cierra la sesión igual que RestApiClient (restApiClient.tsx): si el token caduca
     * durante una exportación la aplicación tiene que comportarse exactamente igual que
     * en cualquier otra llamada al API.
     */
    const handleUnauthorizedExport = (status: number): void => {
        restApiClient.closeSession();

        if (status === 418) {
            //Logout, la sesión ha expirado
            window.sessionStorage.setItem("session-expired", "true");
            window.location.href = `${(IsLaguagePresentInUrl() ? GetLanguageInUrl() : '')}`;
        } else {
            //Logout, no está autorizado a realizar la operación que ha intentado realizar
            window.sessionStorage.setItem("no-authorized", "true");
            window.location.href = "/forbiden";
        }
    };

    /**
     * Exporta SIEMPRE el filtro que tenga puesto la usuaria en el formulario.
     * Si llega un preset, se aplica encima de ese filtro.
     */
    const handleExport = async (preset?: BookingExportPreset) => {
        setExporting(true);

        try {
            const response = await exportBookings(buildFilter(form.getFieldsValue()), preset);

            if (response?.isFile && response.blob) {
                // El nombre lo pone el front: el navegador no puede leer Content-Disposition.
                saveBlobAsFile(response.blob, getExportFileName());
            } else if (response && (response.status === 401 || response.status === 418)) {
                // Sesión caducada o sin permisos: el 401 puede venir sin cuerpo, así que
                // nunca debe acabar mostrando el mensaje de "no hay reservas".
                handleUnauthorizedExport(response.status);
            } else {
                // El backend puede responder HTTP 200 con un JSON de error (por ejemplo,
                // demasiadas filas), por eso se mira el contenido y no el status.
                const message = response?.json?.message;

                notification.error({
                    message: t("components.bookings.filter.export-error")!,
                    description: (message && message.length > 0)
                        ? message
                        : (response?.json
                            // Solo hay JSON del backend: es su respuesta de "no hay filas".
                            ? t("components.bookings.filter.export-no-results")!
                            // Respuesta ilegible (HTML de un proxy, 500, cuerpo vacío...).
                            : t("components.bookings.filter.export-error-description")!),
                });
            }
        } catch (error) {
            console.error('Error exporting bookings:', error);

            notification.error({
                message: t("components.bookings.filter.export-error")!,
                description: t("components.bookings.filter.export-error-description")!,
            });
        } finally {
            setExporting(false);
        }
    };

    const onMenuClick = ({ key }: any) => {
        handleExport(getExportPreset(key));
    };

    return (
        <div className="bookings-filter">

            {/* ── Cabecera ───────────────────────────────────────────── */}
            <div className="filter-header">
                <span className="filter-title">
                    <FilterOutlined /> {t("components.bookings.filter.buscador")}
                </span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                        danger
                        onClick={() => setDeleteModalVisible(true)}
                        icon={<DeleteOutlined />}
                    >
                        {t("components.bookings.filter.delete-pending-bookings")}
                    </Button>
                    <Dropdown.Button
                        disabled={exporting}
                        loading={exporting}
                        menu={{ items, onClick: onMenuClick }}
                        className="export-button-dropdown"
                        icon={<DownOutlined />}
                        onClick={() => handleExport()}
                    >
                        {exporting
                            ? t("components.bookings.filter.exporting")
                            : <><FileExcelOutlined className="excel-button" /> {t("components.bookings.filter.export-data")}</>
                        }
                    </Dropdown.Button>

                </div>
            </div>

            {/* ── Formulario ─────────────────────────────────────────── */}
            <Form form={form} layout="vertical" onFinish={onFinish}>

                {/* Fila principal */}
                <Row gutter={[12, 0]} align="bottom">
                    <Col xs={24} sm={12} md={5}>
                        <Form.Item name="locator" label={t("components.bookings.filter.locator")} initialValue={''}>
                            <Input className="app-input" allowClear placeholder={t("components.bookings.filter.locator") || ''} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Form.Item name="status" label={t("components.bookings.filter.status")} initialValue={''}>
                            <Select className="app-input" allowClear placeholder={t("forms.selects.select-else") || ''}>
                                <Select.Option value="">{t("forms.selects.select-else")}</Select.Option>
                                {status.map((item, i) => (
                                    <Select.Option key={i} value={item.key}>{item.label}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Form.Item name="agency" label={t("components.bookings.filter.agency")} initialValue={''}>
                            <Input className="app-input" allowClear placeholder={t("components.bookings.filter.agency") || ''} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Form.Item name="checkin" label={t("components.bookings.filter.check-in")} initialValue={null}>
                            <DatePicker allowClear className="app-input" format="YYYY-MM-DD" placeholder={t("components.bookings.filter.date-picker") || ""} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Form.Item name="checkout" label={t("components.bookings.filter.check-out")} initialValue={null}>
                            <DatePicker allowClear className="app-input" format="YYYY-MM-DD" placeholder={t("components.bookings.filter.date-picker") || ""} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={2}>
                        <Form.Item label=" ">
                            <Button
                                className="app-button search-btn"
                                htmlType="submit"
                                disabled={searching}
                                loading={searching}
                                icon={<SearchOutlined />}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Toggle búsqueda avanzada */}
                <div className="advanced-toggle" onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}>
                    {showAdvancedSearch ? <UpOutlined /> : <DownOutlined />}
                    <span>{t("components.bookings.filter.advanced-search")}</span>
                </div>

                {/* Búsqueda avanzada */}
                {showAdvancedSearch && (
                    <Row gutter={[12, 0]} className="advanced-row">
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="name" label={t("components.bookings.filter.name")} initialValue={''}>
                                <Input allowClear className="app-input" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="hotels" label={t("components.bookings.filter.hotels")} initialValue={[]}>
                                <Select className="app-input" allowClear mode="multiple" placeholder={t("forms.selects.select-else") || ''} optionFilterProp="children">
                                    {hotelList.map((item: HotelInfo, i) => (
                                        <Select.Option key={i} value={item.id}>{item.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="ownLocator" label={t("components.bookings.filter.own-locator")} initialValue={''}>
                                <Input allowClear className="app-input" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="userEmail" label={t("components.bookings.filter.email")} initialValue={''}>
                                <Input allowClear className="app-input" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
            </Form>

            {deleteModalVisible &&
                <GenericModal
                    isLoading={deletingPending}
                    title={t("components.bookings.filter.delete-pending-bookings")}
                    closable
                    content={<div>{t("components.bookings.filter.delete-pending-confirm")}</div>}
                    visible={deleteModalVisible}
                    okDanger
                    onOk={handleDeletePendingBookings}
                    onCancel={() => setDeleteModalVisible(false)}
                />
            }
            </div>
    );
}

export default BookingsFilterComponent;
